import uuidv4 from 'uuid/v4';
import {
  ArticleCreationInput,
  ArticleStatus,
  ArticleUpdateInput,
  GetArticleQuery_query_article_Article,
  UpsertArticleMutationVariables,
} from '../../__generated__/queries-web';
import { MediaBlock, RawDraft } from '../../types/draft';

export const computeUpsertArticleVariables = (
  serverVersionArticle: GetArticleQuery_query_article_Article,
  article: GetArticleQuery_query_article_Article,
  slideMap: Record<string, MediaBlock>,
  slidesIndex: string[],
): UpsertArticleMutationVariables => {
  const newBody: RawDraft = {
    blocks: slidesIndex.map((id) => ({
      ...slideMap[id],
      depth: slideMap[id]?.depth ?? 0,
      inlineStyleRanges: slideMap[id]?.inlineStyleRanges ?? [],
      entityRanges: slideMap[id]?.entityRanges ?? [],
    })),
    entityMap: [],
  };

  const createArticleData: ArticleCreationInput = {
    title: article.title,
    format: article.format,
    status: article.status,
    body: newBody,
    brandKey: article.brandKey,
    publishedAt: article.publishedAt
      ? new Date(article.publishedAt).toISOString()
      : null,
    ...(article.editedAt || article.status === ArticleStatus.Published
      ? {
          editedAt:
            article.status === ArticleStatus.Published
              ? new Date().toISOString()
              : article.editedAt
              ? new Date(article.editedAt).toISOString()
              : new Date().toISOString(),
        }
      : {}),
    tags: {
      create: article.tags.map((articleTag, idx) => {
        const { title, type, relationId } = articleTag.tag;
        return {
          order: idx + 1,
          tag: {
            createIfNotExists: {
              where: { relationId, type },
              data: {
                title,
                type,
                relationId,
              },
            },
          },
        };
      }),
    },
    qualifiers: {
      create: article.qualifiers.map((qualifier, i) => ({
        order: i + 1,
        qualifier: { connect: { id: qualifier.qualifier.id } },
      })),
    },
    source: article.source
      ? {
          connect: { id: article.source.id },
        }
      : undefined,
  };

  const updateArticleData: ArticleUpdateInput = {
    ...(() => {
      const { format, brandKey, ...rest } = createArticleData;
      return rest;
    })(),
    tags: {
      ...createArticleData.tags,
      delete: serverVersionArticle.tags.map((articleTag) => ({
        tag: { id: articleTag.tag.id },
        order: articleTag.order,
      })),
    },
    qualifiers: {
      ...(serverVersionArticle.qualifiers.length > 0 && {
        delete: Array.from(
          { length: serverVersionArticle.qualifiers.length },
          (_, i) => ({
            order: i + 1,
          }),
        ),
      }),
      create: article.qualifiers.map((qualifier, i) => ({
        order: i + 1,
        qualifier: { connect: { id: qualifier.qualifier.id } },
      })),
    },
    ...(article.source && {
      source: {
        connect: { id: article.source.id },
      },
    }),
  };

  if (article.categories.length) {
    const categoryCreate = article.categories.every((cat) => cat.category.id)
      ? {
          create: [
            {
              category: {
                connect: {
                  id: article.categories[0].category.id,
                },
              },
            },
          ],
        }
      : {};

    // eslint-disable-next-line immutable/no-mutation
    createArticleData.categories = categoryCreate;

    // eslint-disable-next-line immutable/no-mutation
    updateArticleData.categories = {
      ...(serverVersionArticle.categories.length > 0 && {
        delete: serverVersionArticle.categories
          .filter(
            (category) =>
              category.order !== undefined && category.order !== null,
          )
          .map((category) => ({
            order: category.order,
          })),
      }),
      ...categoryCreate,
    };
  }

  return {
    create: createArticleData,
    update: updateArticleData,
    where: { id: article.id || uuidv4() },
  };
};
